import { useEffect, useMemo, useState } from "react";
import { useNavigate, useOutletContext } from "react-router-dom";
import { ReactComponent as EmptyBox } from "../../../assets/icons/emptybox.svg";
import {
  alpha,
  Button,
  Grid,
  IconButton,
  InputAdornment,
  Paper,
  Stack,
  TextField,
  Typography,
  useTheme,
} from "@mui/material";
import { Box } from "@mui/material";
import { RowsDataSource } from "../../../componentes/administracao/types";
import api from "../../../services/api";
import { AxiosError } from "axios";
import { useNotification } from "../../../componentes/notificationProvider";
import { Add, ChevronLeft, ChevronRight, Search } from "@mui/icons-material";
import { LoadingPage } from "../../loading";
import CardDataSource from "../../../componentes/administracao/dataSource/cards";
import NewAlertModal from "../../../componentes/newAlertModal";

type OutletContextType = {
  setSubTitle: (subtitle: string) => void;
  setIsEditingDataSource: React.Dispatch<React.SetStateAction<boolean>>;
};

export default function DataSource() {
  const { setSubTitle, setIsEditingDataSource } =
    useOutletContext<OutletContextType>();
  const [loadingPage, setLoadingPage] = useState(false);
  const [searchText, setSearchText] = useState("");
  const [rows, setRows] = useState<RowsDataSource[]>([]);
  const [page, setPage] = useState(1);
  const [openDeleteModal, setOpenDeleteModal] = useState<boolean>(false);
  const [selectedDataSource, setSelectedDataSource] =
    useState<RowsDataSource | null>(null);

  const itemsPerPage = 4;
  const theme = useTheme();
  const notify = useNotification();
  const navigate = useNavigate();

  useEffect(() => {
    setSubTitle("Data Source");
  }, [setSubTitle]);

  useEffect(() => {
    async function fetchDataSources() {
      try {
        setLoadingPage(true);
        const response = await api.get("/datasource/findMany");
        setRows(response.data);
      } catch (error) {
        console.error(error);
        if (error instanceof AxiosError)
          notify(error.status || 500, undefined, "error", error);
        else notify("Não foi possível se conectar ao servidor!", "error");
      } finally {
        setLoadingPage(false);
      }
    }
    fetchDataSources();
  }, []);
  const styles = {
    button: {
      fontSize: "13px",
      flexWrap: "no-wrap",
      height: "40px",
      backgroundColor: alpha(theme.palette.primary.main, 0.2),
      color: "primary.main",
    },
    iconEmptyBox: {
      fill: theme.palette.text.disabled,
    },
    boxEmpty: {
      display: "flex",
      justifyContent: "center",
      alignItems: "center",
      flexDirection: "column",
      height: "100%",
      width: "100%",
      "&.MuiCollapse-wrapper ": {
        height: "100%",
      },
      "&.MuiCollapse-root": {
        height: "100%",
      },
      "&.MuiCollapse-root .MuiCollapse-vertical": {
        height: "100%",
      },
    },
    subtitle: {
      color: theme.palette.text.disabled,
      mt: 2,
      width: "500px",
      textAlign: "center",
    },
    containerBox: {
      padding: 3,
      minWidth: "320px",
      alignItems: "flex-start",
      display: "flex",
      flexDirection: "column",
      justifyContent: "flex-start",
      height: "100%",
    },
    paper: {
      padding: 2,
      gap: 2,
      width: "100%",
      flexDirection: "column",
      display: "flex",
      alignItems: "center",
      flexWrap: "wrap",
      minWidth: "320px",
      height: "100%",
      maxHeight: 600,
    },
    stackContainer: {
      backgroundColor: theme.palette.background.default,
      display: "flex",
      flexDirection: "column",
      width: "100%",
      borderRadius: 1,
      padding: 2,
    },
    boxHeader: {
      width: "100%",
      justifyContent: "space-between",
      display: "flex",
      flexDirection: "row",
      flexWrap: "wrap",
      gap: 2,
      alignItems: "center",
    },
    boxSearchAndFilter: {
      width: { xs: "100%", md: "100%" },
      flexWrap: { xs: "nowrap", md: "wrap" },
      gap: { xs: 1, md: 2 },
      display: "flex",
      justifyContent: "space-between",
      alignItems: "center",
    },
    searchInput: {
      width: { xs: "100%", md: "400px" },
    },
    stackPagination: {
      display: "flex",
      flexDirection: "row",
      alignItems: "center",
      justifyContent: "space-between",
      width: "100%",
      height: "100%",
      maxHeight: "58px",
      backgroundColor: "background.paper",
      padding: 1.5,
      gap: 1,
      borderRadius: 1,
    },
    iconBtns: { display: "flex", flexDirection: "row" },
  };
  const handleDeleteDataSource = async (id: string) => {
    try {
      const response = await api.post<RowsDataSource | null>(
        "/datasource/delete",
        {
          id: id,
        },
      );
      setRows((prev) => prev.filter((d) => d.id !== response?.data?.id));
    } catch (error) {
      if (error instanceof AxiosError)
        notify(error?.status || 500, error.message, "error", error);
      else notify("Não foi possível se conectar ao servidor!", "error");
    }
  };
  const filteredRows = useMemo(() => {
    return rows.filter((row) => {
      const search = searchText.toLowerCase();
      const matchesSearch = row.name && row.name.toLowerCase().includes(search);

      return matchesSearch;
    });
  }, [searchText, rows]);

  const paginatedRows = useMemo(() => {
    const lastIndex = page * itemsPerPage;
    const firstIndex = lastIndex - itemsPerPage;
    return filteredRows.slice(firstIndex, lastIndex);
  }, [filteredRows, page, itemsPerPage]);
  const totalPages = Math.ceil(filteredRows.length / itemsPerPage);
  return (
    <>
      <Box sx={styles.containerBox}>
        <Paper elevation={0} sx={styles.paper}>
          {loadingPage ? (
            <LoadingPage />
          ) : (
            <>
              <Box sx={styles.boxHeader}>
                <Box sx={styles.boxSearchAndFilter}>
                  <TextField
                    size="small"
                    label="Pesquisar Data source"
                    variant="filled"
                    sx={styles.searchInput}
                    value={searchText}
                    onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                      setSearchText(event.target.value);
                    }}
                    autoComplete="off"
                    slotProps={{
                      input: {
                        endAdornment: (
                          <InputAdornment position="end">
                            <Search />
                          </InputAdornment>
                        ),
                      },
                    }}
                  />

                  <Button
                    startIcon={<Add />}
                    onClick={() => {
                      setIsEditingDataSource(true);
                      navigate("/configuracoes/datasource/criar");
                    }}
                    sx={styles.button}
                    variant="contained"
                  >
                    Adicionar
                  </Button>
                </Box>
              </Box>

              <Stack sx={styles.stackContainer}>
                {rows && rows.length > 0 ? (
                  <Grid
                    container
                    spacing={2}
                    sx={{
                      justifyContent: { xs: "center", sm: "start" },
                      width: "100%",
                      height: "100%",
                    }}
                  >
                    {Array.isArray(paginatedRows) &&
                      paginatedRows.length > 0 &&
                      paginatedRows.map((row, index) => (
                        <Grid key={index} size={12}>
                          <CardDataSource
                            {...row}
                            setIsEditingDataSource={setIsEditingDataSource}
                            setOpenDeleteModal={setOpenDeleteModal}
                            setSelectedDataSource={setSelectedDataSource}
                          />
                        </Grid>
                      ))}

                    <Grid
                      key={"pagination-datasource"}
                      size={12}
                      sx={{
                        display: "flex",
                        flexDirection: "column",
                        justifyContent: "flex-end",
                        paddingY: 2,
                      }}
                    >
                      <Stack sx={styles.stackPagination}>
                        <Typography variant="body2" color="text.secondary">
                          Página {page} de {totalPages || 1}
                        </Typography>

                        <Box sx={styles.iconBtns}>
                          <IconButton
                            disabled={page === 1}
                            onClick={() => setPage((prev) => prev - 1)}
                            size="small"
                          >
                            <ChevronLeft />
                          </IconButton>
                          <IconButton
                            disabled={page >= totalPages}
                            onClick={() => setPage((prev) => prev + 1)}
                            size="small"
                          >
                            <ChevronRight />
                          </IconButton>
                        </Box>
                      </Stack>
                    </Grid>
                  </Grid>
                ) : (
                  <Box sx={styles.boxEmpty}>
                    <EmptyBox style={styles.iconEmptyBox} />
                    <Typography variant="body1" sx={styles.subtitle}>
                      Nenhum data source encontrado. Crie um novo data source
                      para visualizá-lo aqui.
                    </Typography>
                  </Box>
                )}
              </Stack>
            </>
          )}
        </Paper>
      </Box>
      <NewAlertModal
        openModal={openDeleteModal}
        setOpenModal={setOpenDeleteModal}
        buttonText="Excluir"
        buttonCancelText="Cancelar"
        confirmar={false}
        handleButton={() => {
          handleDeleteDataSource(String(selectedDataSource?.id));
          setOpenDeleteModal(false);
        }}
        handleCancel={() => {
          setOpenDeleteModal(false);
        }}
        severity="error"
        singleButton={false}
        textContent={`Tem certeza que deseja excluir o Data Source ${selectedDataSource?.name}?`}
      />
    </>
  );
}
